import DialogComp from 'components/Dialog';
import AccessGroupsForm from 'components/Forms/AccessGroups';
import ToolTipComponent from 'components/ToolTip';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { EditIcon, PlusIcon, TrashIcon } from 'lucide-react';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

type TVaultAccessRequiredFields = 'groupName'; //  | 'externalGroups' | 'capabilities' | 'isDefaultGroup' // The rest are automatically set to a default value

const hasRequiredFields = (data: Partial<TVaultAccessGroup>): data is Record<TVaultAccessRequiredFields, string> => {
  if ('groupName' in data && data.groupName) return true;
  return false;
};

const AccessGroups = ({ accessGroups = [], newAccessGroups = [], onChangeGroups }: IAccessGroups) => {
  const [filterString, setFilterString] = React.useState<string>('');

  const filteredGroups = (newAccessGroups.length > 0 ? newAccessGroups : accessGroups || []).filter(u => {
    if (!filterString) return u;
    return u.groupName.toLowerCase().includes(filterString);
  });

  const [groupData, setGroupData] = React.useState<Partial<TVaultAccessGroup> | null>(null);

  const onAddNewGroup = () => {
    if (!groupData) return;
    const newGroupData = {
      ...groupData,
      groupId: groupData.groupId || uuidv4(),
      action: 'new' as const,
      externalGroups:
        typeof groupData.externalGroups === 'string' && groupData.externalGroups
          ? (groupData.externalGroups as string).split(/,\s*/)
          : groupData.externalGroups || [],
      capabilities:
        typeof groupData.capabilities === 'string' && groupData.capabilities
          ? (groupData.capabilities as string).split(/,\s*/)
          : groupData.capabilities || [],
      isDefaultGroup: !!groupData.isDefaultGroup,
    };
    onChangeGroups([
      ...(newAccessGroups.length > 0 ? newAccessGroups : accessGroups),
      newGroupData,
    ] as IOrg['accessGroups']);
    setGroupData(null);
  };

  const onEditExistingGroup = () => {
    if (!groupData) return;
    const oldData = accessGroups.find(grp => grp.groupId === groupData.groupId);
    const formattedNewData = {
      ...(groupData.externalGroups
        ? {
            externalGroups:
              typeof groupData.externalGroups === 'string'
                ? (groupData.externalGroups as string).split(/,\s*/)
                : groupData.externalGroups,
          }
        : {}),
      ...(groupData.capabilities
        ? {
            capabilities:
              typeof groupData.capabilities === 'string'
                ? (groupData.capabilities as string).split(/,\s*/)
                : groupData.capabilities,
          }
        : {}),
      ...(groupData.isDefaultGroup || groupData.isDefaultGroup === false
        ? {
            isDefaultGroup: groupData.isDefaultGroup,
          }
        : {}),
    };

    const group = {
      ...oldData,
      ...groupData,
      action: 'edit' as const,
      ...formattedNewData,
    };
    const newGroups = (newAccessGroups.length > 0 ? newAccessGroups : accessGroups).map(g =>
      g.groupId === group.groupId ? group : g,
    );
    onChangeGroups(newGroups as IOrg['accessGroups']);
    setGroupData(null);
  };

  const deletedGroups = newAccessGroups.filter(grp => grp.action === 'remove');

  return (
    <div>
      <div className="mb-2 flex items-end">
        <div>
          <Input
            type="text"
            id="groupFilter"
            className="w-100"
            value={filterString}
            onChange={e => setFilterString(e.target.value)}
            placeholder="Search"
          />
        </div>
        <div className="flex w-full justify-end">
          <DialogComp
            openButton={
              <Button variant="outline" className="h-9">
                <PlusIcon strokeWidth={2} />
                <div>New access group</div>
              </Button>
            }
            title="New access group"
            content={
              <AccessGroupsForm
                data={{}}
                newData={groupData || {}}
                onChangeData={(field, data) => setGroupData({ ...groupData, [field]: data })}
              />
            }
            cancel={{ callback: () => setGroupData({}) }}
            accept={{
              callback: () => onAddNewGroup(),
              disabled: !groupData || !hasRequiredFields(groupData),
            }}
          />
        </div>
      </div>
      <div className="tableArea rounded-md">
        <table>
          <thead>
            <tr>
              <th>Group name</th>
              <th>External groups</th>
              <th>Capabilities</th>
              <th>Is default group</th>
              <th style={{ width: 'auto' }}></th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.map(grp => {
              const isDeleted = deletedGroups.find(du => du.groupId === grp.groupId);
              const isNew = newAccessGroups.length > 0 && !accessGroups.find(nu => nu.groupId === grp.groupId);
              return (
                <tr key={grp.groupId} className={isDeleted ? 'line-through' : isNew ? 'font-bold italic' : ''}>
                  <td>{grp.groupName}</td>
                  <td>{grp.externalGroups?.join(', ')}</td>
                  <td>
                    {(grp.capabilities || [])?.length > 0 ? (
                      <ToolTipComponent
                        trigger={grp.capabilities?.length + ' capabilities'}
                        content={<div className="max-w-240">{grp.capabilities?.join(', ')}</div>}
                      />
                    ) : (
                      ''
                    )}
                  </td>
                  <td>{grp.isDefaultGroup ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="flex w-full justify-end">
                      <DialogComp
                        openButton={
                          <Button size={'icon'}>
                            <TrashIcon />
                          </Button>
                        }
                        title="Do you really want to delete this access group?"
                        cancel={{ callback: () => '' }}
                        accept={{
                          callback: () => {
                            const newGroups = (newAccessGroups.length > 0 ? newAccessGroups : accessGroups).map(g =>
                              g.groupId === grp.groupId ? { ...g, action: 'remove' as const } : g,
                            );
                            onChangeGroups(newGroups as IOrg['accessGroups']);
                          },
                        }}
                      />

                      <DialogComp
                        openButton={
                          <Button size={'icon'} className="ml-2">
                            <EditIcon />
                          </Button>
                        }
                        title="Edit access group"
                        content={
                          <AccessGroupsForm
                            data={grp}
                            newData={groupData || {}}
                            onChangeData={(field, data) =>
                              setGroupData({ ...groupData, groupId: grp.groupId, [field]: data })
                            }
                          />
                        }
                        cancel={{
                          callback: () => {
                            setGroupData(null);
                          },
                        }}
                        accept={{
                          callback: () => onEditExistingGroup(),
                          disabled: !groupData || !hasRequiredFields({ ...grp, ...groupData }),
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface IAccessGroups {
  /** Existing accessgroups. Use optional type here to mitigate some crashes due to possible missing data */
  accessGroups: Optional<TVaultAccessGroup, 'externalGroups' | 'capabilities'>[];
  newAccessGroups?: IOrg['accessGroups'];
  onChangeGroups: (accessGroups: IOrg['accessGroups']) => void;
}

export default AccessGroups;
